<template>
    <!--
    We put the slotted trigger inside a <span> so we can:
      - Attach mouseenter/mouseleave listeners
      - Grab a ref for measuring its bounding rect
    Using "display: contents" means this <span> won't add any layout box.
  -->
    <span
        ref="trigger"
        :class="$style.triggerElement"
        @mouseenter="showTooltip"
        @mouseleave="hideTooltip"
        @focus="showTooltip"
        @blur="hideTooltip"
    >
        <slot />
    </span>

    <teleport
        v-if="isVisible"
        to="body"
    >
        <!-- eslint-disable vue/no-v-html -->
        <div
            ref="tooltip"
            :class="[
                $style.tooltip,
                $style[`tooltip-${adjustedPosition}`],
                $style[`tooltip-${adjustedPosition}-${props.theme}`],
                props.theme ? $style[`tooltip-theme-${props.theme}`] : ``,
            ]"
            :style="tooltipStyle"
            v-html="content"
        />
        <!--eslint-enable-->
    </teleport>
</template>

<script setup lang="ts">
import { defineProps, nextTick, onBeforeUnmount, onMounted, ref, watch, withDefaults } from 'vue'

type Position = 'top' | 'bottom' | 'left' | 'right'

interface Props {
    content?: string
    position?: Position
    offset?: number
    theme?: 'green' | 'dark'
}

const ARROW_OFFSET = 6

// we want to have a gap between the tooltip and the window edge
const WINDOW_EDGE_PADDING = 10

const props = withDefaults(defineProps<Props>(), {
    position: 'top',
    offset: 22,
    theme: 'green',
})

const trigger = ref<HTMLElement | null>(null)
const tooltip = ref<HTMLElement | null>(null)
const isVisible = ref(false)
const showTimeout = ref<number | null>(null)

const adjustedPosition = ref<Position>(props.position)

const updatePosition = () => {
    const triggerElement = trigger.value?.firstElementChild
    if (!tooltip.value || !triggerElement) {
        return
    }

    const tooltipRect = tooltip.value.getBoundingClientRect()
    const triggerRect = triggerElement.getBoundingClientRect()

    // Get window dimensions and scroll position
    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth

    // Reset to original position
    adjustedPosition.value = props.position
    const space = {
        top: triggerRect.top, // Space from top of viewport
        bottom: windowHeight - triggerRect.bottom, // Space from bottom of viewport
        left: triggerRect.left, // Space from left of viewport
        right: windowWidth - triggerRect.right, // Space from right of viewport
    }

    const offset = props.offset + ARROW_OFFSET + WINDOW_EDGE_PADDING

    // Check if there's enough space in the preferred position
    switch (props.position) {
        case 'top':
            if (space.top < tooltipRect.height + offset) {
                adjustedPosition.value = 'bottom'
            }
            break
        case 'bottom':
            if (space.bottom < tooltipRect.height + offset) {
                adjustedPosition.value = 'top'
            }
            break
        case 'left':
            if (space.left < tooltipRect.width + offset) {
                adjustedPosition.value = 'right'
            }
            break
        case 'right':
            if (space.right < tooltipRect.width + offset) {
                adjustedPosition.value = 'left'
            }
            break
        default:
            break
    }
}

const hideTooltip = () => {
    if (showTimeout.value) {
        clearTimeout(showTimeout.value)
        showTimeout.value = null
    }
    isVisible.value = false
}

const showTooltip = () => {
    if (showTimeout.value) {
        clearTimeout(showTimeout.value)
    }
    // we want to have a delay before the tooltip is shown
    showTimeout.value = window.setTimeout(() => {
        isVisible.value = true
    }, 100)
}

const tooltipStyle = ref({})

const calculateTooltipStyle = () => {
    const triggerElement = trigger.value?.firstElementChild
    if (!triggerElement) {
        return
    }

    const rect = triggerElement?.getBoundingClientRect()

    let top = 0
    let left = 0
    let transform = ''

    const offset = props.offset + ARROW_OFFSET

    switch (adjustedPosition.value) {
        case 'top':
            top = rect.top - offset
            left = rect.left + rect.width / 2
            transform = 'translate(-50%, -100%)'
            break
        case 'bottom':
            top = rect.bottom + offset
            left = rect.left + rect.width / 2
            transform = 'translate(-50%, 0)'
            break
        case 'left':
            top = rect.top + rect.height / 2
            left = rect.left - offset
            transform = 'translate(-100%, -50%)'
            break
        case 'right':
            top = rect.top + rect.height / 2
            left = rect.right + offset
            transform = 'translate(0, -50%)'
            break
        default:
            break
    }

    tooltipStyle.value = {
        position: 'fixed' as const,
        top: `${top}px`,
        left: `${left}px`,
        transform,
    }
}

const handleScroll = () => {
    hideTooltip()
}

// Update position when tooltip becomes visible
watch(isVisible, (visible) => {
    if (visible) {
        // Wait for tooltip to render
        nextTick(() => {
            updatePosition()
            calculateTooltipStyle()
        })
    }
})

// Update position on window resize
onMounted(() => {
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', handleScroll, true) // true for capture phase to handle all scrollable parents
})

onBeforeUnmount(() => {
    if (showTimeout.value) {
        clearTimeout(showTimeout.value)
    }
    window.removeEventListener('resize', updatePosition)
    window.removeEventListener('scroll', handleScroll, true)
})
</script>

<style module>
.triggerElement {
    display: contents;
}

@keyframes slide-top {
    from {
        translate: 0 10px;
        opacity: 0;
    }

    to {
        translate: 0 0;
        opacity: 1;
    }
}

@keyframes slide-right {
    from {
        translate: 10px 0;
        opacity: 0;
    }

    to {
        translate: 0 0;
        opacity: 1;
    }
}

@keyframes slide-bottom {
    from {
        translate: 0 -10px;
        opacity: 0;
    }

    to {
        translate: 0 0;
        opacity: 1;
    }
}

@keyframes slide-left {
    from {
        translate: -10px 0;
        opacity: 0;
    }

    to {
        translate: 0 0;
        opacity: 1;
    }
}

.tooltip {
    font-weight: var(--font-weight-accent);
    position: fixed;
    backdrop-filter: blur(1px);
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 16px;
    z-index: 1000;
    pointer-events: none;
    width: max-content;
    max-width: 400px;
    animation-duration: 0.3s;

    white-space: pre-line;
}

.tooltip-theme-green {
    background: var(--electric-lime);
    color: var(--black);
}

.tooltip-theme-dark {
    color: var(--white);
    background-color: var(--carbon-400);
}

.tooltip-top {
    animation-name: slide-top;
}

.tooltip-bottom {
    animation-name: slide-bottom;
}

.tooltip-left {
    animation-name: slide-left;
}

.tooltip-right {
    animation-name: slide-right;
}

.tooltip::after {
    content: '';
    position: absolute;
    border: 6px solid transparent;
}

.tooltip-top-green::after {
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-top-color: var(--electric-lime);
}

.tooltip-top-dark::after {
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-top-color: var(--carbon-400);
}

.tooltip-bottom-green::after {
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-bottom-color: var(--electric-lime);
}
.tooltip-bottom-dark::after {
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-bottom-color: var(--carbon-400);
}

.tooltip-left-green::after {
    left: calc(100% - 1px);
    top: 50%;
    transform: translateY(-50%);
    border-left-color: var(--electric-lime);
}
.tooltip-left-dark::after {
    left: calc(100% - 1px);
    top: 50%;
    transform: translateY(-50%);
    border-left-color: var(--carbon-400);
}

.tooltip-right-green::after {
    right: calc(100% - 1px);
    top: 50%;
    transform: translateY(-50%);
    border-right-color: var(--electric-lime);
}
.tooltip-right-dark::after {
    right: calc(100% - 1px);
    top: 50%;
    transform: translateY(-50%);
    border-right-color: var(--carbon-400);
}
</style>
